// Package Imports
import React, { useEffect, useState } from 'react';
import { DevtoolsProvider, DevtoolsPanel } from '@refinedev/devtools';
import { notificationProvider } from '@refinedev/antd';
import { Refine } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import routerBindings, {
  // DocumentTitleHandler,
  UnsavedChangesNotifier,
} from '@refinedev/react-router-v6';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// import { ConfigProvider } from 'antd';
import { ConfigProvider, App, Spin } from 'antd';
/* eslint-disable import/no-extraneous-dependencies */
import {
  StyleProvider,
  createCache,
  legacyLogicalPropertiesTransformer,
} from '@ant-design/cssinjs';
// Project Imports
import AppRoutes from 'core/routes/routes';
import ConfigStyle from 'core/constants/ConfigStyle';
import resources from 'core/resources/resources';
import customDataProvider from 'providers/DataProvder';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import DashboardResource from 'core/resources/Dashboard';
import useLogger from 'core/utils/useLogger';
import {
  REACT_APP_API_URL,
  REACT_APP_API_URL_ORG,
  REACT_APP_API_URL_ROLE_PERM,
  REACT_APP_VENDOR_API_URL,
} from 'config';
import { useKeycloak } from '@react-keycloak/web';
import authProvider from 'providers/AuthProvider';
import Loading from 'components/loading/loading';
import { Images } from 'core/common/imagePath';
// type NewDataProvider = DataProvider & {
//   getApiUrl?: () => string;
// };

type I18nProviderType = {
  translate: (key: string, options?: Record<string, unknown>) => string;
  changeLocale: (lang: string) => Promise<void>;
  getLocale: () => string; // Add other methods or properties with their respective types
};

const nonce = 'osian9839980';

const styleCache = createCache();
if (nonce) {
  (styleCache as any).nonce = nonce;
}

if (nonce) {
  (ConfigProvider.config as any)({
    csp: { nonce },
  });
}

// // Read nonce once globally
// const nonce =
//   (document.querySelector('meta[name="csp-nonce"]') as HTMLMetaElement)
//     ?.content || 'nonce-osian9839980';

// // Configure global AntD CSP context (for Modal.confirm, notification, etc.)
// (AntConfig.config as any)({
//   csp: { nonce },
// });

const Initiate = () => {
  const [keycloakReady, setKeycloakReady] = useState(false);
  const { keycloak, initialized } = useKeycloak();
  // const customTitleHandler = () => 'User Management';

  const { filteredResources, setToken, setUserSessionId } = useTokenContext();

  const { t, i18n } = useTranslation();
  const logger = useLogger();
  const i18nProvider: I18nProviderType = {
    translate: (key: string, options?: Record<string, unknown>) =>
      t(key, options),
    async changeLocale(lang: string): Promise<void> {
      await i18n.changeLanguage(lang); // Optionally handle any additional logic after changing the language
    },
    getLocale: () => i18n.language,
  };

  const userManagementDataProvider = customDataProvider(REACT_APP_API_URL);
  const organizationDataProvider = customDataProvider(REACT_APP_API_URL_ORG);
  const rolePermissionDataProvider = customDataProvider(
    REACT_APP_API_URL_ROLE_PERM
  );

  const vendorManagementDataProvider = customDataProvider(
    REACT_APP_VENDOR_API_URL
  );

  const resource = [{ ...DashboardResource }, ...filteredResources];
  logger('Resources Fetched for User', resources);

  useEffect(() => {
    console.log('Keycloak initialization status:', {
      initialized,
      authenticated: keycloak?.authenticated,
    });

    if (initialized) {
      console.log('Keycloak initialized, proceeding...');
      setKeycloakReady(true);
    }
  }, [initialized, keycloak]); // Added keycloak to dependencies

  // Debugging logs
  console.log('Current render state:', {
    initialized,
    keycloakReady,
    keycloak: !!keycloak,
    authenticated: keycloak?.authenticated,
  });

  if (!keycloakReady) {
    console.log('Showing loading state');
    return <Loading />;
  }

  if (!keycloak?.authenticated) {
    console.log('User not authenticated');
    return <h1>User not authenticated</h1>;
  }

  console.log('Rendering main app');
  Spin.setDefaultIndicator(
    <img
      style={{ width: '100px', height: '100px' }}
      src={Images.loaderGif}
      alt="loading"
    />
  );

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        {/* <StyleProvider hashPriority="high" transformers={[]}> */}
        {/* <ConfigProvider csp={{ nonce: 'osian9839980' }} {...ConfigStyle}> */}
        <StyleProvider
          hashPriority="high"
          cache={styleCache}
          transformers={[legacyLogicalPropertiesTransformer]}
        >
          <ConfigProvider csp={{ nonce }} {...ConfigStyle}>
            <App>
              <DevtoolsProvider>
                <Refine
                  dataProvider={{
                    default: userManagementDataProvider,
                    organizationDataProvider,
                    rolePermissionDataProvider,
                    vendorManagementDataProvider,
                  }}
                  i18nProvider={i18nProvider}
                  notificationProvider={notificationProvider}
                  routerProvider={routerBindings}
                  authProvider={authProvider(
                    keycloak,
                    setToken,
                    setUserSessionId
                  )}
                  resources={resource}
                  options={{
                    syncWithLocation: false,
                    warnWhenUnsavedChanges: false,
                    projectId: 'yFsPTU-0WFGC2-VwzawS',
                  }}
                >
                  <AppRoutes />
                  <RefineKbar />
                  <UnsavedChangesNotifier />
                  {/* <DocumentTitleHandler handler={customTitleHandler} /> */}
                </Refine>
                <DevtoolsPanel />
              </DevtoolsProvider>
            </App>
          </ConfigProvider>
        </StyleProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
};

export default Initiate;
